import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import { Document, Page, pdfjs } from 'react-pdf';
import { knowledgeBasesApi } from '../utils/api';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface KBResourcePreviewProps {}

const KBResourcePreview: React.FC<KBResourcePreviewProps> = () => {
  const { kbId, resourceId } = useParams<{ kbId: string; resourceId: string }>();

  const [fileData, setFileData] = useState<Blob | null>(null);
  const [fileInfo, setFileInfo] = useState<{ name: string; type: string } | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [imageZoom, setImageZoom] = useState<number>(100);

  useEffect(() => {
    loadFile();
  }, [kbId, resourceId]);

  const loadFile = async () => {
    if (!kbId || !resourceId) return;

    try {
      setLoading(true);

      // Get KB info first to get resource name
      const kb = await knowledgeBasesApi.getById(parseInt(kbId));
      let fileName = 'unknown';

      for (const resource of kb.resources || []) {
        if (resource.id === parseInt(resourceId)) {
          fileName = resource.resource_name;
          break;
        }
      }

      // Download file blob
      const blob = await knowledgeBasesApi.downloadResource(parseInt(kbId), parseInt(resourceId));
      const fileType = getFileType(fileName);

      setFileData(blob);
      setFileInfo({ name: fileName, type: fileType });

      // For text-based files, read content
      if (['code', 'markdown', 'text', 'json', 'html'].includes(fileType)) {
        const text = await blob.text();
        setFileContent(text);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load resource');
      setLoading(false);
    }
  };

  const getFileType = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    // Code files
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'sh', 'bash', 'sql', 'css', 'scss', 'less'].includes(ext)) {
      return 'code';
    }

    // Markdown
    if (ext === 'md') return 'markdown';

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(ext)) return 'image';

    // PDF
    if (ext === 'pdf') return 'pdf';

    // HTML
    if (ext === 'html' || ext === 'htm') return 'html';

    // JSON
    if (ext === 'json') return 'json';

    // Plain text
    if (ext === 'txt' || ext === 'log') return 'text';

    return 'binary';
  };

  const getLanguage = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const langMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'jsx',
      'ts': 'typescript',
      'tsx': 'tsx',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'sh': 'bash',
      'bash': 'bash',
      'sql': 'sql',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'md': 'markdown',
      'html': 'markup',
      'xml': 'xml',
    };
    return langMap[ext] || 'text';
  };

  const handleDownload = () => {
    if (!fileData || !fileInfo) return;

    const url = window.URL.createObjectURL(fileData);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileInfo.name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const renderPreview = () => {
    if (!fileInfo || !fileData) return null;

    switch (fileInfo.type) {
      case 'code':
      case 'json':
        return (
          <div className="bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
            <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
              <span className="text-gray-300 text-sm font-mono">{fileInfo.name}</span>
              <span className="text-gray-400 text-xs">{getLanguage(fileInfo.name)}</span>
            </div>
            <SyntaxHighlighter
              language={getLanguage(fileInfo.name)}
              style={vscDarkPlus}
              showLineNumbers
              wrapLines
              customStyle={{
                margin: 0,
                padding: '1.5rem',
                fontSize: '0.875rem',
                lineHeight: '1.5',
              }}
            >
              {fileContent}
            </SyntaxHighlighter>
          </div>
        );

      case 'markdown':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-4">
                <h2 className="text-white font-semibold text-lg">{fileInfo.name}</h2>
              </div>
              <div className="prose prose-lg max-w-none p-8 markdown-content">
                <ReactMarkdown>{fileContent}</ReactMarkdown>
              </div>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="flex flex-col items-center">
            <div className="mb-4 flex items-center gap-4 bg-white rounded-lg shadow-lg px-6 py-3">
              <button
                onClick={() => setImageZoom(Math.max(25, imageZoom - 25))}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                disabled={imageZoom <= 25}
              >
                -
              </button>
              <span className="text-sm font-medium min-w-[60px] text-center">{imageZoom}%</span>
              <button
                onClick={() => setImageZoom(Math.min(200, imageZoom + 25))}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                disabled={imageZoom >= 200}
              >
                +
              </button>
              <button
                onClick={() => setImageZoom(100)}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm"
              >
                Reset
              </button>
            </div>
            <div className="bg-gray-100 rounded-lg shadow-2xl p-8 overflow-auto max-h-[70vh]">
              <img
                src={URL.createObjectURL(fileData)}
                alt={fileInfo.name}
                style={{ width: `${imageZoom}%`, maxWidth: 'none' }}
                className="rounded"
              />
            </div>
          </div>
        );

      case 'pdf':
        return (
          <div className="flex flex-col items-center">
            <div className="mb-4 flex items-center gap-4 bg-white rounded-lg shadow-lg px-6 py-3">
              <button
                onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm font-medium">
                Page {pageNumber} of {numPages}
              </span>
              <button
                onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                disabled={pageNumber >= numPages}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="bg-white rounded-lg shadow-2xl p-4">
              <Document
                file={fileData}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={<div className="text-center py-8">Loading PDF...</div>}
              >
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>
          </div>
        );

      case 'html':
        return (
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
              <span className="text-gray-300 text-sm font-mono">{fileInfo.name}</span>
            </div>
            <iframe
              srcDoc={fileContent}
              className="w-full h-[70vh]"
              sandbox="allow-scripts"
              title={fileInfo.name}
            />
          </div>
        );

      case 'text':
        return (
          <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
            <div className="bg-gray-100 px-6 py-4 border-b">
              <h2 className="font-semibold">{fileInfo.name}</h2>
            </div>
            <pre className="p-6 text-sm font-mono overflow-auto max-h-[70vh]">
              {fileContent}
            </pre>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="inline-block p-6 bg-white rounded-lg shadow-lg">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-600 mb-4">No preview available for this file type</p>
              <button
                onClick={handleDownload}
                className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Download File
              </button>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-red-600 mb-4">{error}</p>
          <p className="text-gray-500 text-sm">You can close this tab to return to the knowledge base.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Top Bar */}
      <div className="bg-white shadow-md border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{fileInfo?.name}</h1>
              <p className="text-sm text-gray-500">Knowledge Base Resource</p>
            </div>

            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Preview Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderPreview()}
      </div>
    </div>
  );
};

export default KBResourcePreview;
